import React, {useCallback, useRef, useState} from "react";
import {Link} from "@renta-apps/athenaeum-react-components";
import {Box, Button, ButtonSize, ButtonType, Checkbox, Icon, Text, ValueIndicator} from "@renta-apps/renta-react-components";
import {formatDate} from "@/helpers/DateHelper";
import styles from "./EquipmentsGrid.module.scss";
import Localizer from "@/localization/Localizer";
import {ProductWithTelematics} from "@/models/server/Models/Equipment/ProductWithTelematics";
import EasyPlusFeatureButton from "@/components/EasyPlus/EasyPlusFeatureButton/EasyPlusFeatureButton";
import {FeatureSwitch} from "@/providers/FeatureSwitch";
import {useClickOutsideEffect} from "@/helpers/Hooks";
import {handleDeviceDetailsLinkClick, handleProductLinkClick} from "@/helpers/NavigationHelper";
import PriceHelper from "@/helpers/PriceHelper";
import RentaEasyConstants from "@/helpers/RentaEasyConstants";
import {capitalizeWords} from "@/helpers/StringHelper";
import {formatMultiLineText} from "@/helpers/TextHelper";
import {ServiceType} from "@/models/Enums";
import InlineTooltip from "@/components/InlineTooltip/InlineTooltip";
import PageDefinitions from "@/providers/PageDefinitions";
import {RentalContract} from "@/models/server/Models/Equipment/RentalContract";
import {RentalContractReturn} from "@/models/server/Models/Equipment/RentalContractReturn";
import {ProductWithTelematicsDetails} from "@/models/server/Models/Equipment/ProductWithTelematicsDetails";
import {RentalInformation} from "@/models/server/Models/Equipment/RentalInformation";
import {ReturnRequestInformation} from "@/models/server/Models/Equipment/ReturnRequestInformation";
import ExpandedInfoField from "./ExpandedInfoField";
import TitleArea from "./TitleArea";

interface IEquipmentsGridRowProps {
    item: ProductWithTelematics;
    index: number;
    canSelectChargingRequest: boolean;
    isEasyPlusUser: boolean;
    expanded: boolean,
    equipmentDetails: ProductWithTelematicsDetails | null,
    onAccordionButtonClick: () => Promise<void>,
    onSubscribeButtonClick: () => void;
    onReturnButtonClick: () => void;
    onServiceRequestButtonClick: (serviceType: ServiceType) => void;
}

const EquipmentsGridRow: React.FC<IEquipmentsGridRowProps> = ({
    item,
    index,
    canSelectChargingRequest,
    isEasyPlusUser,
    expanded,
    equipmentDetails,
    onAccordionButtonClick,
    onSubscribeButtonClick,
    onReturnButtonClick,
    onServiceRequestButtonClick,
}) => {
    const [expandedContracts, setExpandedContracts] = useState<{ [key: string]: boolean }>({});
    const [serviceRequestDropdownVisible, setServiceRequestDropdownVisible] = useState(false);
    const serviceRequestDropdownRef = useRef<HTMLDivElement>(null);
    const serviceRequestButtonRef = useRef<HTMLDivElement>(null);

    const handleDeviceNameClick = useCallback(() => {
        if ((!item.rentaId || item.isReturned) && !item.url) {
            return;
        }

        item.url && (item.isMassProduct || item.isReturned)
            ? handleProductLinkClick(item.url)
            : handleDeviceDetailsLinkClick(item.rentaId!);
    }, [item]);

    useClickOutsideEffect([serviceRequestDropdownRef, serviceRequestButtonRef], serviceRequestDropdownVisible, () => setServiceRequestDropdownVisible(false));

    const formatPrice = (deviceDetails: RentalInformation | RentalContract | null, units: number = 1): string => {

        if (!deviceDetails) {
            return '-';
        }

        if (units === 0) {
            return '-';
        }

        let {dailyPrice, monthlyPrice, dailyRentalType, rentType} = deviceDetails;

        // Divide by units only if the price is not null to calculate the price per unit.
        dailyPrice = dailyPrice !== null ? dailyPrice / units : null;
        monthlyPrice = monthlyPrice !== null ? monthlyPrice / units : null;

        return PriceHelper.formatRentPrice(dailyPrice, monthlyPrice, dailyRentalType, rentType);
    };

    const handleOnAccordionTitleButtonClick = (index: string) => {
        setExpandedContracts(prev => ({
          ...prev,
          [index]: !prev[index],
        }));
    };

    const handleServiceRequestButtonClick = (serviceType: ServiceType) => {
        onServiceRequestButtonClick(serviceType);
        setServiceRequestDropdownVisible(false);
    };

    return (
        <div className={styles.dataRow}>
            <div className={`${styles.gridRow} ${styles.main}`}>
                <div className={styles.name} data-cy={`device-name-${index}`}>
                    <div className={styles.checkboxCell}>
                        <Checkbox checked={false} onChange={() => {}}/>

                        {item.isReturnRequested &&
                            <InlineTooltip
                                text={Localizer.fleetMonitoringPageGridTooltipReturnRequested}
                                icon={<Icon name="truck" size={14}/>}
                                className={styles.tooltipIcon}
                                noOpacity={true}
                                onClick={(event) => event.stopPropagation()}
                            />
                        }
                    </div>

                    <div className={`${styles.gridChild} ${styles.fontRegular} ${item.isReturnRequested ? styles.paddingLeft19 : ''}`}>
                        {(!item.isReturned && (item.rentaId || item.url)) ? (
                            <span className={`${styles.link} ${styles.ellipsisText}`}
                                  onClick={handleDeviceNameClick}
                            >
                                {capitalizeWords(item.deviceName)}
                            </span>
                        ) : (
                            <span className={`${styles.ellipsisText} ${item.isReturned ? styles.disabled : ''}`}>{capitalizeWords(item.deviceName)}</span>
                        )}
                    </div>
                </div>
                <div className={styles.rentaId} data-cy={`renta-id-${index}`}>
                    <span className={styles.label}>{Localizer.fleetMonitoringPageGridId}</span>
                    <span>{item.rentaId ?? '-'}</span>
                </div>
                <div className={styles.actions}>
                    <FeatureSwitch flagName={RentaEasyConstants.featureFlagRentalReturnRequest}>
                        <Button className={styles.actionsButton}
                                size={ButtonSize.Small}
                                type={ButtonType.Icon}
                                disabled={item.isReturnRequested || item.isReturned}
                                onClick={onReturnButtonClick}
                                data-cy={`return-button-${index}`}
                        >
                            <Icon name="truck" size={20}/>
                        </Button>
                    </FeatureSwitch>
                    <FeatureSwitch flagName={RentaEasyConstants.featureFlagServicesView}>
                        <div ref={serviceRequestButtonRef}>
                            <Button className={styles.actionsButton}
                                    size={ButtonSize.Small}
                                    type={ButtonType.Icon}
                                    disabled={item.isReturned}
                                    onClick={() => setServiceRequestDropdownVisible(prev => !prev)}
                                    data-cy={`service-request-button-${index}`}
                            >
                                <Icon name="tools" size={20}/>
                            </Button>
                        </div>
                    </FeatureSwitch>
                    <EasyPlusFeatureButton className={styles.actionsButton}
                                           size={ButtonSize.Small}
                                           type={ButtonType.Icon}
                                           disabled={!item.supportsAlerts || item.isReturned}
                                           onClick={onSubscribeButtonClick}
                                           data-cy={`subscribe-button-${index}`}
                    >
                        <Icon name="gear" size={20}/>
                    </EasyPlusFeatureButton>
                    {serviceRequestDropdownVisible && (
                        <div className={styles.serviceRequestDropdown} ref={serviceRequestDropdownRef}>
                            <div onClick={() => handleServiceRequestButtonClick(ServiceType.Refuel)}>
                                {Localizer.enumServiceTypeRefuel}
                            </div>
                            <div onClick={() => handleServiceRequestButtonClick(ServiceType.Maintenance)}>
                                {Localizer.enumServiceTypeMaintenance}
                            </div>
                            {canSelectChargingRequest && (
                                <div onClick={() => handleServiceRequestButtonClick(ServiceType.Charging)}>
                                    {Localizer.enumServiceTypeCharging}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.expand}>
                    <div className={styles.accordionButton}
                         onClick={onAccordionButtonClick}
                         data-cy={`accordion-button-${index}`}
                    >
                        <i className={`fas fa-angle-down ${expanded ? styles.rotated : ""}`}></i>
                    </div>
                </div>
            </div>
            {(isEasyPlusUser && item.isTrackingDevice && !item.isReturned) && (
                <div className={`${styles.gridRow} ${styles.telematics}`} data-cy={`telematics-${index}`}>
                    <div className={styles.telematicsContainer}>
                        {item.alertsCount !== null && (
                            <div className={styles.telematicsElement}>
                                <ValueIndicator size="extra-small" type="alerts" value={item.alertsCount}/>
                            </div>
                        )}
                        {item.idleDays !== null && (
                            <div className={styles.telematicsElement}>
                                <ValueIndicator size="extra-small" type="idle" value={item.idleDays}/>
                            </div>
                        )}
                        {item.batteryLevel !== null && (
                            <div className={styles.telematicsElement}>
                                <ValueIndicator size="extra-small" type="battery" value={item.batteryLevel}/>
                            </div>
                        )}
                        {item.fluidLevel !== null && (
                            <div className={styles.telematicsElement}>
                                <ValueIndicator size="extra-small" type="fluid" value={item.fluidLevel}/>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className={`${styles.gridRow} ${styles.expandableGridRow} ${expanded ? styles.expandedRow : ""}`} data-cy="row-details">
                {equipmentDetails?.rentalInformation ? (
                    <>
                        <Box display="flex" flexDirection="column" data-cy={"device-info"}>
                            <TitleArea equipmentDetails={equipmentDetails} label={Localizer.equipmentGridDeviceInfo} mode="device"/>
                            <div className={styles.values}>
                                <div className={styles.row}>
                                    <div className={styles.expandedInfoField}>
                                        <Text tag={"label"} size={"Normal"} weight="medium">
                                            {Localizer.productProductCategory}
                                        </Text>
                                        <span className={styles.ellipsisText} data-cy="product-group">
                                            {(() => {
                                                const productName = capitalizeWords(
                                                    equipmentDetails.localizedProductName?.name?.trim() || Localizer.genericNameUnknown
                                                );

                                                return equipmentDetails.localizedProductName?.url ? (
                                                    <Link className={styles.link} route={PageDefinitions.productDetails.route({ id: equipmentDetails.localizedProductName.url })}>
                                                        {productName}
                                                    </Link>
                                                ) : (
                                                    <Text tag="label">{productName}</Text>
                                                );
                                            })()}
                                        </span>
                                    </div>
                                    <div className={styles.expandedInfoField}>
                                        <Text tag={"label"} size={"Normal"} weight="medium">
                                            {Localizer.equipmentGridCategory}
                                        </Text>
                                        <span className={styles.ellipsisText} data-cy="category">
                                            {(() => {
                                                const categoryName = capitalizeWords(
                                                    equipmentDetails.localizedCategoryName?.name?.trim() || Localizer.genericNameUnknown
                                                );

                                                return equipmentDetails.localizedCategoryName?.url ? (
                                                    <Link className={styles.link} route={PageDefinitions.rent.route({ id: equipmentDetails.localizedCategoryName.url })}>
                                                        {categoryName}
                                                    </Link>
                                                ) : (
                                                    <Text tag="label">{categoryName}</Text>
                                                );
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Box>

                        <Box display="flex" flexDirection="column" data-cy={"rental-information"}>
                            <TitleArea equipmentDetails={equipmentDetails} label={Localizer.fleetMonitoringPageGridRentalInformation} mode="device"/>
                            <div className={styles.values}>
                                <div className={styles.row}>
                                    <ExpandedInfoField label={Localizer.fleetMonitoringPageGridRentalStartDate}
                                                        value={formatDate(equipmentDetails.rentalInformation.rentalStartDate)}
                                                        dataCy="rental-start-date"
                                    />
                                    <ExpandedInfoField label={equipmentDetails.rentalInformation.estimatedRentalEndDate ? Localizer.equipmentGridRentalEstimatedEndDate : Localizer.equipmentGridRentalEndDate}
                                                        value={formatDate(equipmentDetails.rentalInformation.estimatedRentalEndDate ?? equipmentDetails.rentalInformation?.rentalEndDate)}
                                                        dataCy="rental-end-date"
                                    />
                                </div>
                                <div className={styles.row}>
                                    <ExpandedInfoField label={Localizer.fleetMonitoringPageGridOrderedBy}
                                                        value={equipmentDetails.rentalInformation.orderedBy}
                                                        dataCy="ordered-by"
                                    />
                                    <ExpandedInfoField label={Localizer.equipmentGridTotalCost}
                                                        value={formatPrice(equipmentDetails.rentalInformation)}
                                                        dataCy="total-cost"
                                    />
                                </div>
                                <div className={styles.row}>
                                    <ExpandedInfoField label={Localizer.equipmentGridContract}
                                                        value={equipmentDetails.rentalInformation.contractNumber}
                                                        dataCy="contract-number"
                                    />
                                    <ExpandedInfoField label={Localizer.fleetMonitoringPageGridAmount}
                                                        value={equipmentDetails.rentalInformation.amount}
                                                        dataCy="amount"
                                    />
                                </div>
                            </div>
                        </Box>

                        {equipmentDetails.returnRequests.map((productDetail: ReturnRequestInformation, index: number) => (
                            <Box key={index} display="flex" flexDirection="column" data-cy={"requested-return-information"}
                            >
                                <TitleArea equipmentDetails={equipmentDetails}
                                           label={
                                               equipmentDetails.returnRequests.length > 1
                                                   ? `${Localizer.equipmentGridReturnRequestInformation} ${index + 1} / ${equipmentDetails.returnRequests.length}`
                                                   : Localizer.equipmentGridReturnRequestInformation
                                            }
                                            mode="rental"
                                            returnRequestId={productDetail.returnRequest?.id}
                                />
                                <div className={styles.values}>
                                    <div className={styles.row}>
                                        <ExpandedInfoField label={Localizer.fleetMonitoringPageGridReturnRequestedAt}
                                                           value={formatDate(productDetail.returnRequest?.requestedAt, false, false)}
                                                           dataCy={`return-requested-at-${index}`}
                                        />
                                        <ExpandedInfoField label={Localizer.fleetMonitoringPageGridAmount}
                                                           value={productDetail.quantity}
                                                           dataCy={`return-amount-${index}`}
                                        />
                                    </div>
                                    <div className={styles.row}>
                                        <ExpandedInfoField label={Localizer.equipmentGridReturnRequestPickUpTime}
                                                           value={formatDate(productDetail.returnRequest?.requestedReturnTime, false, false)}
                                                           dataCy={`return-pick-up-time-${index}`}
                                        />
                                        <ExpandedInfoField label={Localizer.fleetMonitoringPageGridReturnRequestedBy}
                                                           value={productDetail.returnRequest?.requestedBy ?? null}
                                                           dataCy={`return-requested-by-${index}`}
                                        />
                                    </div>
                                </div>
                            </Box>
                        ))}

                        {equipmentDetails.rentalContracts.map((contract: RentalContract, index: number) => (
                            <Box key={index} display="flex" flexDirection="column" data-cy={"contract-information"}>
                                <TitleArea equipmentDetails={equipmentDetails}
                                           label={`${Localizer.equipmentGridContract} ${contract.contractNumber}`}
                                           mode="contract"
                                           expanded={expandedContracts[contract.contractNumber!]}
                                           onAccordionTitleAreaButtonClick={() => handleOnAccordionTitleButtonClick(contract.contractNumber!)}
                                />
                                <div className={`${styles.values} ${styles.expandableValues} ${expandedContracts[contract.contractNumber!] ? styles.expandedValues : ""}`}>
                                    <div className={styles.row}>
                                        <ExpandedInfoField label={Localizer.fleetMonitoringPageGridRentalStartDate}
                                                           value={formatDate(contract.rentalStartDate)}
                                                           dataCy={`contract-start-date-${index}`}
                                        />
                                        <ExpandedInfoField label={contract.estimatedRentalEndDate ? Localizer.equipmentGridRentalEstimatedEndDate : Localizer.equipmentGridRentalEndDate}
                                                           value={formatDate(contract.estimatedRentalEndDate ?? contract.rentalEndDate)}
                                                           dataCy={`contract-end-date-${index}`}
                                        />
                                    </div>
                                    <div className={styles.row}>
                                        <ExpandedInfoField label={Localizer.fleetMonitoringPageGridOrderedBy}
                                                           value={contract.orderedBy}
                                                           dataCy={`ordered-by-${index}`}
                                        />
                                        <ExpandedInfoField label={Localizer.equipmentGridTotalCost}
                                                           value={formatPrice(contract)}
                                                           dataCy={`total-cost-${index}`}
                                        />
                                    </div>
                                    <div className={styles.row}>
                                        <ExpandedInfoField label={Localizer.equipmentGridUnitCost}
                                                           value={formatPrice(contract, contract.amountRentedInitially ?? 1)}
                                                           dataCy={`unit-cost-${index}`}
                                        />
                                        <ExpandedInfoField label={Localizer.equipmentGridAmountRentedInitially}
                                                           value={contract.amountRentedInitially}
                                                           dataCy={`amount-rented-initially-${index}`}
                                        />
                                    </div>
                                    <div className={styles.row}>
                                        <ExpandedInfoField label={Localizer.equipmentGridTotalAmountReturned}
                                                           value={contract.totalReturned}
                                                           dataCy={`total-returned-${index}`}
                                        />
                                        <ExpandedInfoField label={Localizer.equipmentGridAmountOnRent}
                                                           value={contract.amountOnRent}
                                                           dataCy={`amount-on-rent-${index}`}
                                        />
                                    </div>
                                    {contract.rentalContractReturns.length > 0 && (<div className={`${styles.row} ${styles.returns}`}>
                                        <Text tag={"label"} size={"Normal"} weight="medium">
                                            {Localizer.equipmentGridReturns}
                                        </Text>
                                        <div className={styles.returnsGrid}>
                                            {contract.rentalContractReturns.map((contractDetailsReturn: RentalContractReturn, index: number) => (
                                                <div key={index}>
                                                    {formatMultiLineText([
                                                        `${Localizer.fleetMonitoringPageGridAmount}: ${contractDetailsReturn.amount}`,
                                                        `${Localizer.equipmentGridDate}: ${formatDate(contractDetailsReturn.rentalDate, false)}`,
                                                        ], styles.ellipsisText)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </Box>
                        ))}

                    </>
                ) : (
                    <div>{Localizer.componentListLoading}</div>
                )}
            </div>
        </div>
    );
};

export default EquipmentsGridRow;